import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Building, Download, Users, Eye, Upload, UserPlus, ChevronDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import DatePicker from "@/components/DatePicker";
import BulkImportPreview from "@/components/BulkImportPreview";
import Pagination from "@/components/Pagination";
import { ContactsTable } from "../components/contacts/ContactsTable";
import { CallHistory, LastCallCell, LogCallButton, QuickLogCallButton, withLastCall, isStaleCall } from "../components/calls/CallHistory";

export default function Clients({ user }) {
  const isSalesTeamMember = user?.department === "Sales" && user?.access_level === "Team Member";
  // Check if user can potentially be a team lead (based on employee record, not user record)
  // We'll call the backend to determine this
  const isSalesTeamLead = user?.department === "Sales" && user?.access_level === "Team Lead";
  const isSalesManager = user?.department === "Sales" && user?.access_level === "Manager";
  const isSalesAdmin = user?.department === "Sales" && user?.access_level === "Admin";
  // Sales users who can delete contacts: only Sales Admin and non-Sales departments
  const canDeleteContacts = !user?.department || user?.department !== "Sales" || isSalesAdmin;
  
  const [clients, setClients] = useState([]);
  const [showTeamData, setShowTeamData] = useState(false); // Toggle state for Team Leads
  const [teamMembers, setTeamMembers] = useState([]); // List of team members for filter
  const [selectedTeamMember, setSelectedTeamMember] = useState("all"); // Selected team member filter
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Contact management states
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [editContactMode, setEditContactMode] = useState(false);
  const [currentContact, setCurrentContact] = useState(null);
  
  // View client details state
  const [statePickerOpen, setStatePickerOpen] = useState(false);
  const [viewClientDialogOpen, setViewClientDialogOpen] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    client_name: "",
    time_zone: "",
    state: "",
    country: "",
    show_my_clients: false  // Filter for sales users
  });
  
  // Bulk import with preview states
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState(null);
  const [importEntityType, setImportEntityType] = useState('Clients');
  const [contactsImportPreviewOpen, setContactsImportPreviewOpen] = useState(false);
  const [contactsPreviewData, setContactsPreviewData] = useState(null);

  // Sales assignment states
  const [clientSalesUsers, setClientSalesUsers] = useState({});  // Map of client_id -> sales user info
  const [users, setUsers] = useState([]);  // All users for general use
  const [salesAssignableUsers, setSalesAssignableUsers] = useState([]);  // Sales/Management users for assignment dropdown
  const [salesAssignmentDialogOpen, setSalesAssignmentDialogOpen] = useState(false);
  const [salesAssignmentClient, setSalesAssignmentClient] = useState(null);
  const [salesAssignmentHistory, setSalesAssignmentHistory] = useState([]);
  const [assignmentFormData, setAssignmentFormData] = useState({
    sales_user_id: "",
    start_date: "",
    notes: ""
  });
  
  // Bulk sales assignment states
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [bulkSalesAssignmentDialogOpen, setBulkSalesAssignmentDialogOpen] = useState(false);
  const [bulkAssignmentFormData, setBulkAssignmentFormData] = useState({
    sales_user_id: "",
    start_date: new Date().toISOString().split('T')[0],
    notes: ""
  });
  
  // Bulk upload error dialog states
  const [uploadErrorsDialogOpen, setUploadErrorsDialogOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccessCount, setUploadSuccessCount] = useState(0);
  
  // Import from approved lead states
  const [approvedLeads, setApprovedLeads] = useState([]);
  const [selectedApprovalId, setSelectedApprovalId] = useState(null);
  
  // Team member IDs cache for team leads (initialize with current user to show toggle initially if user is a lead)
  const [teamMemberIds, setTeamMemberIds] = useState([user?.id]);
  const [teamDataLoaded, setTeamDataLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    client_number: "",
    client_name: "",
    client_type: "Standard",
    industry: "Fabricator",
    software: [],
    business_relation_start_date: "",
    phone: "",
    fax: "",
    website: "",
    time_zone: "EST",
    billing_approval: "90",
    billing_fabrication: "10",
    billing_completion: "0",
    billing_cycle_days: "30",
    billing_address: "",  // Legacy field
    billing_address_line1: "",
    billing_address_line2: "",
    billing_city: "",
    billing_zip: "",
    country: "",
    state: "",
    currency: "USD",
    ccn_hourly_rate: "50"
  });

  const [contactFormData, setContactFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",  // Legacy field
    business_phone: "",
    business_extension: "",
    mobile_phone: "",
    email: "",
    designation: "",
    roles: [],
    special_notes: ""
  });

  const [currencyWarning, setCurrencyWarning] = useState("");

  const softwareOptions = ["SDS/2", "Tekla", "Advance Steel", "AutoCAD", "SolidWorks"];
  const roleOptions = ["Project", "Billing", "Management", "FDO", "Estimator", "Owner"];

  // Function to get currency based on country
  const getCurrencyForCountry = (country) => {
    if (country === "United States") {
      setCurrencyWarning("");
      return "USD";
    } else if (country === "Canada") {
      setCurrencyWarning("");
      return "CAD";
    } else {
      setCurrencyWarning("No default currency available for this country. Please select manually.");
      return formData.currency; // Keep existing currency
    }
  };

  // Handle country change with auto-currency selection
  const handleCountryChange = (country) => {
    const newCurrency = getCurrencyForCountry(country);
    const newCcnRate = newCurrency === "USD" ? "50" : newCurrency === "CAD" ? "65" : formData.ccn_hourly_rate;
    setFormData({ 
      ...formData, 
      country: country, 
      state: "",
      currency: newCurrency,
      ccn_hourly_rate: newCcnRate
    });
  };

  // Format phone number to XXX-XXX-XXXX
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedNumber = phoneNumber.slice(0, 10);
    
    // Format as XXX-XXX-XXXX
    if (limitedNumber.length <= 3) {
      return limitedNumber;
    } else if (limitedNumber.length <= 6) {
      return `${limitedNumber.slice(0, 3)}-${limitedNumber.slice(3)}`;
    } else {
      return `${limitedNumber.slice(0, 3)}-${limitedNumber.slice(3, 6)}-${limitedNumber.slice(6)}`;
    }
  };

  // Handle phone input with validation and formatting
  const handlePhoneInput = (field, value) => {
    // Only allow digits and hyphens, remove any alphabets
    const cleanValue = value.replace(/[^0-9-]/g, '');
    const formattedNumber = formatPhoneNumber(cleanValue);
    setContactFormData({ ...contactFormData, [field]: formattedNumber });
  };
  
  // Countries list with USA and Canada at top
  const countries = [
    "United States",
    "Canada",
    "---", // Separator
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
    "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
    "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
    "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Cape Verde", "Central African Republic",
    "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus",
    "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "East Timor", "Ecuador",
    "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia", "Fiji", "Finland",
    "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala",
    "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia",
    "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya",
    "Kiribati", "North Korea", "South Korea", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
    "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Macedonia", "Madagascar",
    "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius",
    "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique",
    "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
    "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru",
    "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
    "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe",
    "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
    "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname",
    "Swaziland", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Togo",
    "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine",
    "United Arab Emirates", "United Kingdom", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela",
    "Vietnam", "Yemen", "Zambia", "Zimbabwe"
  ];
  
  // US States
  const usStates = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
    "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
    "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri",
    "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island",
    "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming"
  ];
  
  // Canadian Provinces
  const canadianProvinces = [
    "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
    "Northwest Territories", "Nova Scotia", "Nunavut", "Ontario", "Prince Edward Island",
    "Quebec", "Saskatchewan", "Yukon"
  ];
  
  // Get states based on selected country
  const getStatesForCountry = (country) => {
    if (country === "United States") return usStates;
    if (country === "Canada") return canadianProvinces;
    return [];
  };

  useEffect(() => {
    fetchClients();
    fetchUsers();
    fetchSalesAssignableUsers();
    fetchApprovedLeads();
    fetchTeamMemberIds(); // Fetch team member IDs for team leads
    if (isSalesTeamLead || isSalesManager) {
      fetchTeamMembersForFilter(); // Fetch team members for filter dropdown
    }
  }, []);
  
  // Debug: Log when toggle changes
  useEffect(() => {
    if (isSalesTeamLead) {
      console.log("Toggle changed - showTeamData:", showTeamData);
      console.log("Team member IDs:", teamMemberIds);
      console.log("Current user ID:", user?.id);
    }
  }, [showTeamData, teamMemberIds]);

  // Fetch team members for filter dropdown (for Team Leads and Managers)
  const fetchTeamMembersForFilter = async () => {
    try {
      const response = await api.get("/teams/my-team-members-details");
      const memberDetails = response.data.team_members || [];
      setTeamMembers(memberDetails);
    } catch (error) {
      console.error("Failed to fetch team members for filter:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data);
      // Fetch current sales users for all clients
      await fetchAllClientSalesUsers();
    } catch (error) {
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };


  const handleApplyImport = async (selectedNew, selectedUpdates) => {
    try {
      const payload = {
        selected_new: selectedNew,
        selected_updates: selectedUpdates,
        new_records: importPreviewData?.new_records || [],
        updates: importPreviewData?.updates || []
      };
      
      const response = await api.post("/import/clients/apply", payload);
      
      toast.success(`Successfully imported ${response.data.imported} and updated ${response.data.updated} clients!`);
      
      if (response.data.errors.length > 0) {
        setUploadErrors(response.data.errors);
        setUploadSuccessCount(response.data.imported + response.data.updated);
        setUploadErrorsDialogOpen(true);
      }
      
      setImportPreviewOpen(false);
      fetchClients();
    } catch (error) {
      toast.error("Failed to apply import");
    }
  };

  const handleApplyContactsImport = async (selectedNew, selectedUpdates) => {
    try {
      const payload = {
        selected_new: selectedNew,
        selected_updates: selectedUpdates,
        new_records: contactsPreviewData?.new_records || [],
        updates: contactsPreviewData?.updates || []
      };
      
      const response = await api.post("/import/contacts/apply", payload);
      
      toast.success(`Successfully imported ${response.data.imported} and updated ${response.data.updated} contacts!`);
      
      if (response.data.errors.length > 0) {
        setUploadErrors(response.data.errors);
        setUploadSuccessCount(response.data.imported + response.data.updated);
        setUploadErrorsDialogOpen(true);
      }
      
      setContactsImportPreviewOpen(false);
      if (selectedClient) {
        fetchContactsForClient(selectedClient.id);
      }
    } catch (error) {
      toast.error("Failed to apply contacts import");
    }
  };


  const fetchAllClientSalesUsers = async () => {
    try {
      const response = await api.get("/clients/current-sales-users");
      setClientSalesUsers(response.data || {});
    } catch (error) {
      console.error("Error fetching sales users:", error);
    }
  };

  // Fetch team member user IDs from backend (for any user - backend determines if they're a team lead)
  const fetchTeamMemberIds = async () => {
    if (!user) {
      setTeamMemberIds([]);
      setTeamDataLoaded(true);
      return;
    }
    
    try {
      console.log("Clients - Fetching team members for user:", user.username);
      const response = await api.get("/teams/my-team-members");
      const ids = response.data.user_ids || [];
      setTeamMemberIds(ids);
      setTeamDataLoaded(true);
      console.log("Clients - Team member IDs fetched:", ids);
      
      // If we got more than just current user's ID, user is a team lead
      if (ids.length > 1) {
        console.log(`✓ User is a Team Lead with ${ids.length} team members`);
      } else {
        console.log(`User is not a team lead (only 1 ID returned)`);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      // Fallback to just current user
      setTeamMemberIds([user.id]);
      setTeamDataLoaded(true);
    }
  };

  // Get team member user IDs for filtering (Sales Team Lead only)
  const getTeamMemberUserIds = () => {
    // Return cached team member IDs, or fallback to current user
    return teamMemberIds.length > 0 ? teamMemberIds : [user?.id];
  };

  const fetchUsers = async () => {
    // Sales users don't have permission to view users list
    if (user?.department === "Sales") {
      console.log("Skipping user fetch for Sales user");
      return;
    }
    
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      // Only log error if not a Sales user
      if (user?.department !== "Sales") {
        console.log("Failed to fetch users - may not have permission");
      }
    }
  };

  const fetchSalesAssignableUsers = async () => {
    try {
      const response = await api.get("/clients/sales-assignable-users");
      setSalesAssignableUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch sales assignable users:", error);
    }
  };

  const fetchApprovedLeads = async () => {
    try {
      const response = await api.get("/lead-approvals/approved");
      setApprovedLeads(response.data);
    } catch (error) {
      console.error("Failed to fetch approved leads:", error);
      // Don't show toast error for this - it's optional functionality
    }
  };

  const handleSelectApprovedLead = async (approvalId) => {
    if (!approvalId || approvalId === "none") {
      setSelectedApprovalId(null);
      resetForm();
      return;
    }

    const approval = approvedLeads.find(a => a.id === approvalId);
    if (!approval) return;

    setSelectedApprovalId(approvalId);
    
    // Populate form with approval data
    setFormData({
      client_number: "", // Will be auto-generated
      client_name: approval.lead_name || "",
      client_type: approval.lead_status || "Standard",
      industry: approval.industry || "Fabricator",
      software: [],
      business_relation_start_date: "",
      phone: approval.phone || "",
      fax: approval.fax || "",
      website: approval.website || "",
      time_zone: approval.time_zone || "EST",
      billing_approval: "90",
      billing_fabrication: "10",
      billing_completion: "0",
      billing_cycle_days: "30",
      billing_address: "",
      billing_address_line1: approval.address_line1 || "",
      billing_address_line2: approval.address_line2 || "",
      billing_city: approval.city || "",
      billing_zip: approval.zip_code || "",
      country: approval.country || "",
      state: approval.state || "",
      currency: "USD",
      ccn_hourly_rate: "50"
    });

    // Populate contacts from approval
    if (approval.contacts && approval.contacts.length > 0) {
      // Note: Contacts will be created via API when client is created
      toast.success(`Lead data loaded. ${approval.contacts.length} contact(s) will be imported.`);
    }
  };

  const fetchContactsForClient = async (clientId) => {
    try {
      const response = await api.get(`/contacts?client_id=${clientId}`);
      setContacts(response.data);
    } catch (error) {
      toast.error("Failed to fetch contacts");
    }
  };

  const handleViewContacts = (client) => {
    setSelectedClient(client);
    setContactsDialogOpen(true);
    fetchContactsForClient(client.id);
  };

  const handleAddContact = () => {
    resetContactForm();
    setEditContactMode(false);
    setContactFormOpen(true);
  };

  const handleEditContact = (contact) => {
    setCurrentContact(contact);
    setContactFormData({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      phone: contact.phone || "",
      business_phone: contact.business_phone || "",
      business_extension: contact.business_extension || "",
      mobile_phone: contact.mobile_phone || "",
      email: contact.email || "",
      designation: contact.designation || "",
      roles: contact.roles || [],
      special_notes: contact.special_notes || ""
    });
    setEditContactMode(true);
    setContactFormOpen(true);
  };

  const handleContactRoleToggle = (role) => {
    setContactFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...contactFormData,
        client_id: selectedClient.id
      };

      if (editContactMode) {
        await api.put(`/contacts/${currentContact.id}`, contactFormData);
        toast.success("Contact updated successfully!");
      } else {
        await api.post("/contacts", payload);
        toast.success("Contact added successfully!");
      }
      
      setContactFormOpen(false);
      fetchContactsForClient(selectedClient.id);
      resetContactForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editContactMode ? 'update' : 'add'} contact`);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success("Contact deleted successfully");
      fetchContactsForClient(selectedClient.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete contact");
    }
  };

  const resetContactForm = () => {
    setContactFormData({
      first_name: "",
      last_name: "",
      phone: "",
      business_phone: "",
      business_extension: "",
      mobile_phone: "",
      email: "",
      designation: "",
      roles: [],
      special_notes: ""
    });
    setEditContactMode(false);
    setCurrentContact(null);
  };

  const getLatestClientNumber = () => {
    if (clients.length === 0) return null;
    const validClients = clients.filter(c => c.client_number && c.client_number.startsWith('M'));
    if (validClients.length === 0) return null;
    
    const sorted = [...validClients].sort((a, b) => {
      const getNumber = (num) => {
        const match = num.replace('M', '');
        return match ? parseInt(match, 10) : 0;
      };
      return getNumber(b.client_number) - getNumber(a.client_number);
    });
    return sorted[0]?.client_number;
  };

  const handleSoftwareToggle = (software) => {
    setFormData(prev => ({
      ...prev,
      software: prev.software.includes(software)
        ? prev.software.filter(s => s !== software)
        : [...prev.software, software]
    }));
  };

  const getBillingTotal = () => {
    return parseFloat(formData.billing_approval || 0) + 
           parseFloat(formData.billing_fabrication || 0) + 
           parseFloat(formData.billing_completion || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const billingTotal = getBillingTotal();
    if (Math.abs(billingTotal - 100) > 0.01) {
      toast.error(`Billing breakup must total 100%. Current total: ${billingTotal.toFixed(2)}%`);
      return;
    }

    try {
      const payload = {
        ...formData,
        billing_approval: parseFloat(formData.billing_approval),
        billing_fabrication: parseFloat(formData.billing_fabrication),
        billing_completion: parseFloat(formData.billing_completion),
        billing_cycle_days: parseInt(formData.billing_cycle_days) || 30,
        business_relation_start_date: formData.business_relation_start_date || null,
        ccn_hourly_rate: parseFloat(formData.ccn_hourly_rate) || (formData.currency === "CAD" ? 65 : 50)
      };

      if (editMode) {
        await api.put(`/clients/${currentClient.id}`, payload);
        toast.success("Client updated successfully!");
        setDialogOpen(false);
        fetchClients();
        resetForm();
      } else {
        // Check for similar clients before creating
        const checkResponse = await api.post("/clients/check-similarity", payload);
        const { has_name_match, has_address_match, similar_clients } = checkResponse.data;
        
        // Error: Name + Address match (block creation)
        if (has_address_match) {
          const matchedClient = similar_clients.find(c => c.address_match);
          toast.error(
            `Cannot create client: A client with the same name and address already exists (${matchedClient.client_number} - ${matchedClient.client_name}). Please verify the details.`,
            { duration: 6000 }
          );
          return;
        }
        
        // Warning: Name match only (allow with confirmation)
        if (has_name_match) {
          const similarNames = similar_clients.map(c => `${c.client_number} - ${c.client_name}`).join(", ");
          const confirmed = window.confirm(
            `⚠️ Warning: Similar client name(s) found:\n${similarNames}\n\nDo you want to proceed with creating this client?`
          );
          if (!confirmed) {
            return;
          }
        }
        
        // Proceed with creation
        const createUrl = selectedApprovalId 
          ? `/clients?from_approval_id=${selectedApprovalId}` 
          : "/clients";
        await api.post(createUrl, payload);
        toast.success(selectedApprovalId 
          ? "Client created successfully from approved lead!" 
          : "Client created successfully!");
        setDialogOpen(false);
        setSelectedApprovalId(null);
        fetchClients();
        fetchApprovedLeads(); // Refresh approved leads list
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} client`);
    }
  };

  const handleEdit = async (rowClient) => {
    // Fetch fresh full record — list endpoint slims billing_address*, fax
    let client = rowClient;
    try {
      const res = await api.get(`/clients/${rowClient.id}`);
      client = res.data;
    } catch (err) {
      console.error("Failed to fetch full client, falling back to list row", err);
    }
    setCurrentClient(client);
    // Check currency warning for existing country
    const country = client.country || "";
    if (country && country !== "United States" && country !== "Canada") {
      setCurrencyWarning("No default currency available for this country. Please select manually.");
    } else {
      setCurrencyWarning("");
    }
    
    setFormData({
      client_number: client.client_number || "",
      client_name: client.client_name || client.company || "",
      client_type: client.client_type || "Standard",
      industry: client.industry || "Fabricator",
      software: client.software || [],
      business_relation_start_date: client.business_relation_start_date || "",
      phone: client.phone || "",
      fax: client.fax || "",
      website: client.website || "",
      time_zone: client.time_zone || "EST",
      billing_approval: (client.billing_approval || 0).toString(),
      billing_fabrication: (client.billing_fabrication || 0).toString(),
      billing_completion: (client.billing_completion || 0).toString(),
      billing_cycle_days: (client.billing_cycle_days || 30).toString(),
      billing_address: client.billing_address || "",
      billing_address_line1: client.billing_address_line1 || "",
      billing_address_line2: client.billing_address_line2 || "",
      billing_city: client.billing_city || "",
      billing_zip: client.billing_zip || "",
      country: client.country || "",
      state: client.state || "",
      currency: client.currency || "USD",
      ccn_hourly_rate: (client.ccn_hourly_rate || (client.currency === "CAD" ? 65 : 50)).toString()
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleViewClient = (client) => {
    setViewingClient(client);
    setViewClientDialogOpen(true);
  };


  // Sales Assignment Functions
  const handleOpenSalesAssignment = async (client) => {
    setSalesAssignmentClient(client);
    setSalesAssignmentDialogOpen(true);
    
    // Fetch assignment history
    try {
      const response = await api.get(`/clients/${client.id}/sales-assignments`);
      setSalesAssignmentHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch assignment history:", error);
      setSalesAssignmentHistory([]);
    }
    
    // Pre-fill with current assignment if exists
    const currentSales = clientSalesUsers[client.id];
    setAssignmentFormData({
      sales_user_id: currentSales?.sales_user_id || "",
      start_date: new Date().toISOString().split('T')[0],  // Today's date
      notes: ""
    });
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    
    if (!assignmentFormData.sales_user_id) {
      toast.error("Please select a sales user");
      return;
    }
    if (!assignmentFormData.start_date) {
      toast.error("Please select a start date");
      return;
    }
    
    try {
      await api.post(`/clients/${salesAssignmentClient.id}/sales-assignments`, assignmentFormData);
      toast.success("Sales user assigned successfully");
      setSalesAssignmentDialogOpen(false);
      
      // Refresh client list to update sales user display
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign sales user");
    }
  };

  const handleEndAssignment = async (assignmentId, clientId) => {
    const endDate = new Date().toISOString().split('T')[0];
    
    try {
      await api.patch(`/clients/${clientId}/sales-assignments/${assignmentId}`, null, {
        params: { end_date: endDate }
      });
      toast.success("Assignment ended successfully");
      
      // Refresh assignment history
      const response = await api.get(`/clients/${clientId}/sales-assignments`);
      setSalesAssignmentHistory(response.data);
      fetchClients();
    } catch (error) {
      toast.error("Failed to end assignment");
    }
  };

  // Bulk sales assignment handler
  const handleBulkSalesAssignment = async (e) => {
    e.preventDefault();

    if (selectedClients.size === 0) {
      toast.error("Please select at least one client");
      return;
    }

    if (!bulkAssignmentFormData.sales_user_id) {
      toast.error("Please select a sales user");
      return;
    }

    if (!bulkAssignmentFormData.start_date) {
      toast.error("Please select a start date");
      return;
    }

    try {
      const payload = {
        sales_user_id: bulkAssignmentFormData.sales_user_id,
        start_date: bulkAssignmentFormData.start_date,
        notes: bulkAssignmentFormData.notes || ""
      };
      
      const response = await api.post(`/clients/bulk-sales-assignment`, {
        client_ids: Array.from(selectedClients),
        assignment_data: payload
      });
      
      toast.success(response.data.message);
      
      // Refresh clients and clear selection
      fetchClients();
      setSelectedClients(new Set());
      setBulkSalesAssignmentDialogOpen(false);
      setBulkAssignmentFormData({
        sales_user_id: "",
        start_date: new Date().toISOString().split('T')[0],
        notes: ""
      });
    } catch (error) {
      console.error("Bulk sales assignment error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to assign sales user to clients");
    }
  };

  // Toggle client selection
  const handleClientSelection = (clientId) => {
    const newSelection = new Set(selectedClients);
    if (newSelection.has(clientId)) {
      newSelection.delete(clientId);
    } else {
      newSelection.add(clientId);
    }
    setSelectedClients(newSelection);
  };

  // Select/deselect all clients on current page
  const handleSelectAllClients = () => {
    if (selectedClients.size === currentPageClients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(currentPageClients.map(c => c.id)));
    }
  };


  const handleDelete = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success("Client deleted successfully");
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const resetForm = () => {
    setFormData({
      client_number: "",
      client_name: "",
      client_type: "Standard",
      industry: "Fabricator",
      software: [],
      business_relation_start_date: "",
      phone: "",
      fax: "",
      website: "",
      time_zone: "EST",
      billing_approval: "90",
      billing_fabrication: "10",
      billing_completion: "0",
      billing_cycle_days: "30",
      billing_address: "",
      billing_address_line1: "",
      billing_address_line2: "",
      billing_city: "",
      billing_zip: "",
      country: "",
      state: "",
      currency: "USD",
      ccn_hourly_rate: "50"
    });
    setCurrencyWarning("");
    setEditMode(false);
    setCurrentClient(null);
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  // Filter clients based on filter criteria (filters ALL clients, not just current page)
  const filteredClients = clients.filter(client => {
    if (filters.no_call_30 && !isStaleCall(client.last_call_at)) return false;
    // Search in both client_name and client_number
    if (filters.client_name) {
      const searchTerm = filters.client_name.toLowerCase();
      const nameMatch = client.client_name?.toLowerCase().includes(searchTerm);
      const numberMatch = client.client_number?.toLowerCase().includes(searchTerm);
      if (!nameMatch && !numberMatch) return false;
    }
    if (filters.time_zone && client.time_zone !== filters.time_zone) return false;
    if (filters.state && client.state !== filters.state) return false;
    if (filters.country && client.country !== filters.country) return false;
    
    // Sales Team Member: show only their assigned clients
    if (isSalesTeamMember) {
      const salesUser = clientSalesUsers[client.id];
      if (!salesUser || salesUser.sales_user_id !== user.id) return false;
    }
    
    // Sales Team Lead or Manager: by default show all team data
    if (isSalesTeamLead || isSalesManager) {
      const salesUser = clientSalesUsers[client.id];
      if (!salesUser) return false;
      
      // Get all team member IDs including the team lead
      const teamMemberIds = getTeamMemberUserIds();
      // Show only clients assigned to team members (including team lead)
      if (!teamMemberIds.includes(salesUser.sales_user_id)) return false;
      
      // If "My Clients" filter is active, further filter to only team lead's own clients
      if (filters.show_my_clients) {
        if (salesUser.sales_user_id !== user.id) return false;
      }
      
      // If a specific team member is selected in filter
      if (selectedTeamMember && selectedTeamMember !== "all") {
        if (salesUser.sales_user_id !== selectedTeamMember) return false;
      }
    }
    
    // "My Clients" filter for non-sales users - show only clients assigned to current user
    if (filters.show_my_clients && !isSalesTeamLead && !isSalesManager && !isSalesTeamMember) {
      const salesUser = clientSalesUsers[client.id];
      if (!salesUser || salesUser.sales_user_id !== user.id) return false;
    }
    
    return true;
  });

  // Pagination: Get current page data (only for display)
  const totalPages = Math.ceil(filteredClients.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageClients = filteredClients.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const clearFilters = () => {
    setFilters({
      client_name: "",
      time_zone: "",
      state: "",
      country: "",
      show_my_clients: false
    });
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await api.get("/export/clients", {
        responseType: 'blob'
      });
      
      if (!(response.data instanceof Blob)) {
        throw new Error("Invalid response type");
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Clients downloaded successfully!");
    } catch (error) {
      console.error("Excel download error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to download clients";
      toast.error(errorMessage);
    }
  };

  const billingTotal = getBillingTotal();
  const billingValid = Math.abs(billingTotal - 100) < 0.01;

  const getClientDisplay = (client) => {
    const number = client.client_number || "N/A";
    const name = client.client_name || client.company || "Unknown";
    return `${number} - ${name}`;
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="clients-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small text-md3-on-surface mb-2">Clients</h1>
            <p className="text-md3-on-surface-variant">Manage client information and billing details</p>
          </div>
          <div className="flex gap-4 items-center">
            <QuickLogCallButton onLogged={(log) => log.entity_type === "client" && setClients((cs) => withLastCall(cs, log))} />
            {/* Hide Download Excel and New Client buttons for Sales Team Members and Team Leads */}
            {!isSalesTeamMember && !isSalesTeamLead && (
              <>
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={handleDownloadExcel} data-testid="download-clients-button">
                  <Download className="mr-2 h-4 w-4" /> Download Excel
                </Button>
                <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-client-button" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                      <Plus className="mr-2 h-4 w-4" /> New Client
                    </Button>
                  </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader>
                  <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editMode ? 'Edit Client' : 'Create New Client'}</DialogTitle>
                  <DialogDescription className="text-md3-body-medium text-gray-600">
                    {editMode ? 'Update client information' : 'Add a new client with comprehensive details'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Import from Approved Lead - Only show in create mode */}
                  {!editMode && approvedLeads.length > 0 && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md3-lg space-y-3">
                      <div>
                        <h4 className="text-md3-body-small font-semibold text-blue-900 mb-2">Import from Approved Lead</h4>
                        <p className="text-xs text-md3-on-surface-variant mb-3">
                          Select an approved lead to pre-fill client information and import contacts
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="approved_lead_select" className="text-md3-label-large text-gray-700 font-medium">Select Approved Lead</Label>
                        <Select
                          value={selectedApprovalId || "none"}
                          onValueChange={handleSelectApprovedLead}
                        >
                          <SelectTrigger id="approved_lead_select">
                            <SelectValue placeholder="Choose an approved lead (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None - Create new client manually</SelectItem>
                            {approvedLeads.map((approval) => (
                              <SelectItem key={approval.id} value={approval.id}>
                                {approval.lead_number} - {approval.lead_name} 
                                {approval.sales_user_name && ` (Sales: ${approval.sales_user_name})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Section 1: Basic Information */}
                  <div className="space-y-6">
                    <div className="border-b pb-2">
                      <h3 className="text-md3-title-large">Basic Information</h3>
                    </div>
                    
                    {!editMode && clients.length > 0 && getLatestClientNumber() && (
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-xs text-blue-700">
                          <strong>Last Client Number:</strong> {getLatestClientNumber()}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="client_number" className="text-md3-label-large text-gray-700 font-medium">Client Number *</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="client_number"
                          placeholder="e.g., M1001 (Manual entry)"
                          value={formData.client_number}
                          onChange={(e) => setFormData({ ...formData, client_number: e.target.value })}
                          disabled={editMode}
                          required
                        />
                        <p className="text-xs text-md3-on-surface-variant">Unique identifier (e.g., M1001, M1002)</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="client_name" className="text-md3-label-large text-gray-700 font-medium">Client Name *</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="client_name"
                          placeholder="Company/Organization name"
                          value={formData.client_name}
                          onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="client_type" className="text-md3-label-large text-gray-700 font-medium">Client Type *</Label>
                        <Select value={formData.client_type} onValueChange={(value) => setFormData({ ...formData, client_type: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="RFQ">RFQ</SelectItem>
                            <SelectItem value="Standard">Standard</SelectItem>
                            <SelectItem value="Premium">Premium</SelectItem>
                            <SelectItem value="Inactive (Out of Business)">Inactive (Out of Business)</SelectItem>
                            <SelectItem value="Rejected (By Client)">Rejected (By Client)</SelectItem>
                            <SelectItem value="Blacklisted (By Milekal)">Blacklisted (By Milekal)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="industry" className="text-md3-label-large text-gray-700 font-medium">Industry *</Label>
                        <Select value={formData.industry} onValueChange={(value) => setFormData({ ...formData, industry: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Fabricator">Fabricator</SelectItem>
                            <SelectItem value="Erector">Erector</SelectItem>
                            <SelectItem value="Architect">Architect</SelectItem>
                            <SelectItem value="Structural Engineer">Structural Engineer</SelectItem>
                            <SelectItem value="Professional Engineer">Professional Engineer</SelectItem>
                            <SelectItem value="Detailer">Detailer</SelectItem>
                            <SelectItem value="General Contractor">General Contractor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Software Used</Label>
                      <div className="grid grid-cols-3 gap-3 p-3 border rounded">
                        {softwareOptions.map((software) => (
                          <div key={software} className="flex items-center space-x-2">
                            <Checkbox
                              id={software}
                              checked={formData.software.includes(software)}
                              onCheckedChange={() => handleSoftwareToggle(software)}
                            />
                            <Label htmlFor={software} className="cursor-pointer text-md3-body-small">{software}</Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="business_relation_start_date" className="text-md3-label-large text-gray-700 font-medium">Business Relation Start Date</Label>
                      <DatePicker
                        id="business_relation_start_date"
                        value={formData.business_relation_start_date}
                        onChange={(e) => setFormData({ ...formData, business_relation_start_date: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Section 2: Contact Information */}
                  <div className="space-y-6">
                    <div className="border-b pb-2">
                      <h3 className="text-md3-title-large">Company Contact Information</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-md3-label-large text-gray-700 font-medium">Phone</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="phone"
                          placeholder="222-333-4444"
                          value={formData.phone}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value.replace(/[^0-9-]/g, ''));
                            setFormData({ ...formData, phone: formatted });
                          }}
                          maxLength={12}
                        />
                        <p className="text-xs text-md3-on-surface-variant mt-1">Format: XXX-XXX-XXXX (numbers only)</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="fax" className="text-md3-label-large text-gray-700 font-medium">Fax</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="fax"
                          placeholder="222-333-4444"
                          value={formData.fax}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value.replace(/[^0-9-]/g, ''));
                            setFormData({ ...formData, fax: formatted });
                          }}
                          maxLength={12}
                        />
                        <p className="text-xs text-md3-on-surface-variant mt-1">Format: XXX-XXX-XXXX (numbers only)</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="website" className="text-md3-label-large text-gray-700 font-medium">Website</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="website"
                          type="text"
                          placeholder="www.example.com"
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        />
                        <p className="text-xs text-md3-on-surface-variant">Enter website without http:// (e.g., www.example.com)</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="time_zone" className="text-md3-label-large text-gray-700 font-medium">Time Zone</Label>
                        <Select value={formData.time_zone} onValueChange={(value) => setFormData({ ...formData, time_zone: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EST">EST - Eastern</SelectItem>
                            <SelectItem value="CST">CST - Central</SelectItem>
                            <SelectItem value="MST">MST - Mountain</SelectItem>
                            <SelectItem value="PST">PST - Pacific</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Billing Information */}
                  <div className="space-y-6">
                    <div className="border-b pb-2">
                      <h3 className="text-md3-title-large">Billing Information</h3>
                    </div>

                    <div className="space-y-2">
                      <Label>Billing Breakup (Must total 100%)</Label>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="billing_approval" className="text-md3-body-small">Approval (%)</Label>
                          <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="billing_approval"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={formData.billing_approval}
                            onChange={(e) => setFormData({ ...formData, billing_approval: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="billing_fabrication" className="text-md3-body-small">Fabrication (%)</Label>
                          <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="billing_fabrication"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={formData.billing_fabrication}
                            onChange={(e) => setFormData({ ...formData, billing_fabrication: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="billing_completion" className="text-md3-body-small">Completion (%)</Label>
                          <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="billing_completion"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={formData.billing_completion}
                            onChange={(e) => setFormData({ ...formData, billing_completion: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className={`text-md3-body-small font-semibold ${
                        billingValid ? 'text-green-600' : 'text-red-600'
                      }`}>
                        Total: {billingTotal.toFixed(2)}% {billingValid ? '✓' : '✗ Must equal 100%'}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="billing_cycle_days" className="text-md3-label-large text-gray-700 font-medium">Billing Cycle (Days)</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="billing_cycle_days"
                          type="number"
                          min="1"
                          value={formData.billing_cycle_days}
                          onChange={(e) => setFormData({ ...formData, billing_cycle_days: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ccn_hourly_rate" className="text-md3-label-large text-gray-700 font-medium">CCN Hourly Rate ($)</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="ccn_hourly_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.ccn_hourly_rate}
                          onChange={(e) => setFormData({ ...formData, ccn_hourly_rate: e.target.value })}
                          placeholder="50.00"
                        />
                        <p className="text-xs text-md3-on-surface-variant">Auto-filled based on currency (USD: $50, CAD: $65)</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="country" className="text-md3-label-large text-gray-700 font-medium">Country</Label>
                        <Select value={formData.country} onValueChange={handleCountryChange}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((country, index) => (
                              country === "---" ? (
                                <div key={index} className="border-t my-1"></div>
                              ) : (
                                <SelectItem key={country} value={country}>
                                  {country}
                                </SelectItem>
                              )
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state" className="text-md3-label-large text-gray-700 font-medium">State/Province</Label>
                        {formData.country === "United States" || formData.country === "Canada" ? (
                          (() => {
                            const states = getStatesForCountry(formData.country);
                            const selected = states.find(s => s === formData.state);
                            return (
                              <Popover open={statePickerOpen} onOpenChange={setStatePickerOpen}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={statePickerOpen}
                                    className="w-full justify-between font-normal bg-white border-gray-300"
                                    data-testid="client-state-select"
                                  >
                                    <span className={selected ? "" : "text-md3-on-surface-variant"}>
                                      {selected || "Search state/province…"}
                                    </span>
                                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                                  <Command>
                                    <CommandInput placeholder="Type to search…" data-testid="client-state-search" />
                                    <CommandList>
                                      <CommandEmpty>No state matches.</CommandEmpty>
                                      <CommandGroup>
                                        {states.map(s => (
                                          <CommandItem
                                            key={s}
                                            value={s}
                                            onSelect={() => {
                                              setFormData({ ...formData, state: s });
                                              setStatePickerOpen(false);
                                            }}
                                            data-testid={`client-state-opt-${s}`}
                                          >
                                            <Check className={`mr-2 h-4 w-4 ${formData.state === s ? "opacity-100" : "opacity-0"}`} />
                                            {s}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            );
                          })()
                        ) : (
                          <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="state"
                            placeholder="Enter state/province"
                            value={formData.state}
                            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                          />
                        )}
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-md3-body-small font-semibold text-md3-on-surface-variant">Billing Address</h4>
                      <div className="space-y-2">
                        <Label htmlFor="billing_address_line1" className="text-md3-label-large text-gray-700 font-medium">Address Line 1 *</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="billing_address_line1"
                          placeholder="Street address, P.O. box"
                          value={formData.billing_address_line1}
                          onChange={(e) => setFormData({ ...formData, billing_address_line1: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="billing_address_line2" className="text-md3-label-large text-gray-700 font-medium">Address Line 2</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="billing_address_line2"
                          placeholder="Apartment, suite, unit, building, floor, etc."
                          value={formData.billing_address_line2}
                          onChange={(e) => setFormData({ ...formData, billing_address_line2: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="billing_city" className="text-md3-label-large text-gray-700 font-medium">City *</Label>
                          <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="billing_city"
                            placeholder="City"
                            value={formData.billing_city}
                            onChange={(e) => setFormData({ ...formData, billing_city: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="billing_zip" className="text-md3-label-large text-gray-700 font-medium">Zip / Postal Code *</Label>
                          <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="billing_zip"
                            placeholder="Zip or postal code"
                            value={formData.billing_zip}
                            onChange={(e) => setFormData({ ...formData, billing_zip: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <p className="text-xs text-md3-on-surface-variant">Note: Country and State are shared with company information above</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="currency" className="text-md3-label-large text-gray-700 font-medium">Currency</Label>
                      <Select value={formData.currency} onValueChange={(value) => {
                        const newCcnRate = value === "USD" ? "50" : value === "CAD" ? "65" : formData.ccn_hourly_rate;
                        setFormData({ ...formData, currency: value, ccn_hourly_rate: newCcnRate });
                      }}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">US Dollar (USD)</SelectItem>
                          <SelectItem value="CAD">Canadian Dollar (CAD)</SelectItem>
                        </SelectContent>
                      </Select>
                      {currencyWarning && (
                        <p className="text-xs text-orange-600 font-medium">{currencyWarning}</p>
                      )}
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" disabled={!billingValid}  >
                    {editMode ? 'Update Client' : 'Create Client'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Contacts Dialog */}
        <Dialog open={contactsDialogOpen} onOpenChange={setContactsDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">
                Contacts for {selectedClient ? getClientDisplay(selectedClient) : ''}
              </DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Manage contact persons for this client
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <Button onClick={handleAddContact} size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                <Plus className="mr-2 h-4 w-4" /> Add Contact
              </Button>

              <ContactsTable
                contacts={contacts}
                onEdit={handleEditContact}
                onDelete={handleDeleteContact}
                canDelete={canDeleteContacts}
                onChanged={() => fetchContactsForClient(selectedClient.id)}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Contact Form Dialog */}
        <Dialog open={contactFormOpen} onOpenChange={setContactFormOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editContactMode ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editContactMode ? 'Update contact information' : 'Add a new contact person'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-md3-label-large text-gray-700 font-medium">First Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="first_name"
                    value={contactFormData.first_name}
                    onChange={(e) => setContactFormData({ ...contactFormData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-md3-label-large text-gray-700 font-medium">Last Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="last_name"
                    value={contactFormData.last_name}
                    onChange={(e) => setContactFormData({ ...contactFormData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="business_phone" className="text-md3-label-large text-gray-700 font-medium">Business Phone</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="business_phone"
                        placeholder="222-333-4444"
                        value={contactFormData.business_phone}
                        onChange={(e) => handlePhoneInput('business_phone', e.target.value)}
                        maxLength={12}
                      />
                      <p className="text-xs text-md3-on-surface-variant mt-1">Format: XXX-XXX-XXXX (numbers only)</p>
                    </div>
                    <div>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="business_extension"
                        placeholder="Ext."
                        value={contactFormData.business_extension}
                        onChange={(e) => setContactFormData({ ...contactFormData, business_extension: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile_phone" className="text-md3-label-large text-gray-700 font-medium">Mobile Phone</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="mobile_phone"
                    placeholder="222-333-4444"
                    value={contactFormData.mobile_phone}
                    onChange={(e) => handlePhoneInput('mobile_phone', e.target.value)}
                    maxLength={12}
                  />
                  <p className="text-xs text-md3-on-surface-variant mt-1">Format: XXX-XXX-XXXX (numbers only)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="text-md3-label-large text-gray-700 font-medium">Email</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="contact_email"
                    type="email"
                    placeholder="email@example.com"
                    value={contactFormData.email}
                    onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="designation" className="text-md3-label-large text-gray-700 font-medium">Designation</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="designation"
                  placeholder="e.g., Project Manager"
                  value={contactFormData.designation}
                  onChange={(e) => setContactFormData({ ...contactFormData, designation: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Role (Multiple possible)</Label>
                <div className="flex gap-4 p-3 border rounded">
                  {roleOptions.map((role) => (
                    <div key={role} className="flex items-center space-x-2">
                      <Checkbox
                        id={`role-${role}`}
                        checked={contactFormData.roles.includes(role)}
                        onCheckedChange={() => handleContactRoleToggle(role)}
                      />
                      <Label htmlFor={`role-${role}`} className="cursor-pointer text-md3-body-small">{role}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="special_notes" className="text-md3-label-large text-gray-700 font-medium">Special Notes</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="special_notes"
                  placeholder="Additional information about this contact..."
                  rows={3}
                  value={contactFormData.special_notes}
                  onChange={(e) => setContactFormData({ ...contactFormData, special_notes: e.target.value })}
                />
              </div>

              <Button type="submit" className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                {editContactMode ? 'Update Contact' : 'Add Contact'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-md3-title-large text-md3-on-surface ">Clients ({filteredClients.length})</CardTitle>
              <div className="flex gap-2">
                {/* Hide Bulk Upload and Templates for all Sales users */}
                {user?.department !== "Sales" && (
                  <>
                    {/* Clients Bulk Upload */}
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" 
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await api.get("/template/clients", { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', 'clients_template.xlsx');
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success("Clients template downloaded!");
                        } catch (error) {
                          toast.error("Failed to download template");
                        }
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Clients Template
                    </Button>
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                       onClick={() => document.getElementById('bulk-upload-clients').click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Clients
                    </Button>
                    <input
                      id="bulk-upload-clients"
                      type="file"
                      accept=".xlsx,.xls"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          // Call preview endpoint
                          const response = await api.post("/import/clients/preview", formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          
                          setImportPreviewData(response.data);
                          setImportEntityType('Clients');
                          setImportPreviewOpen(true);
                          
                          e.target.value = '';
                        } catch (error) {
                          toast.error("Failed to preview import");
                          e.target.value = '';
                        }
                      }}
                    />
                    
                    {/* Contacts Bulk Upload */}
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" 
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await api.get("/template/contacts", { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', 'contacts_template.xlsx');
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success("Contacts template downloaded!");
                        } catch (error) {
                          toast.error("Failed to download template");
                        }
                      }}
                    >
                  <Download className="h-4 w-4 mr-2" />
                  Contacts Template
                </Button>
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                   onClick={() => document.getElementById('bulk-upload-contacts').click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Contacts
                </Button>
                <input
                  id="bulk-upload-contacts"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    try {
                      // Call preview endpoint
                      const response = await api.post("/import/contacts/preview", formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      
                      setContactsPreviewData(response.data);
                      setContactsImportPreviewOpen(true);
                      
                      e.target.value = '';
                    } catch (error) {
                      toast.error("Failed to preview contacts import");
                      e.target.value = '';
                    }
                  }}
                />
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filter Section */}
            <div className="mb-6 p-4 bg-md3-surface rounded-md3-lg border border-md3-outline-variant">
              <h3 className="text-md3-body-small font-semibold mb-3 text-md3-on-surface-variant">Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="filter_client_name" className="text-xs">Client Name/Number</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_client_name"
                    placeholder="Search by name or number..."
                    value={filters.client_name}
                    onChange={(e) => setFilters({ ...filters, client_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_country" className="text-xs">Country</Label>
                  <Select value={filters.country || "all"} onValueChange={(value) => setFilters({ ...filters, country: value === "all" ? "" : value, state: "" })}>
                    <SelectTrigger id="filter_country">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {countries.filter(c => c !== "---").map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_state" className="text-xs">State/Province</Label>
                  {filters.country === "United States" || filters.country === "Canada" ? (
                    <Select value={filters.state || "all"} onValueChange={(value) => setFilters({ ...filters, state: value === "all" ? "" : value })}>
                      <SelectTrigger id="filter_state">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All States</SelectItem>
                        {getStatesForCountry(filters.country).map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_state"
                      placeholder="Search by state..."
                      value={filters.state}
                      onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                      disabled={!filters.country}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_time_zone" className="text-xs">Time Zone</Label>
                  <Select value={filters.time_zone || "all"} onValueChange={(value) => setFilters({ ...filters, time_zone: value === "all" ? "" : value })}>
                    <SelectTrigger id="filter_time_zone">
                      <SelectValue placeholder="All Time Zones" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time Zones</SelectItem>
                      <SelectItem value="EST">EST</SelectItem>
                      <SelectItem value="CST">CST</SelectItem>
                      <SelectItem value="MST">MST</SelectItem>
                      <SelectItem value="PST">PST</SelectItem>
                      <SelectItem value="AST">AST</SelectItem>
                      <SelectItem value="HST">HST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-md3-on-surface-variant">
                  Showing {currentPageClients.length} of {filteredClients.length} clients
                </p>
                <div className="flex gap-2 items-center">
                  {/* Team Member Filter - Only for Team Leads and Managers */}
                  {(isSalesTeamLead || isSalesManager) && teamMembers.length > 0 && (
                    <Select
                      value={selectedTeamMember}
                      onValueChange={(value) => {
                        setSelectedTeamMember(value);
                        // Reset "My Clients" filter when selecting a team member
                        if (value !== "all") {
                          setFilters({ ...filters, show_my_clients: false });
                        }
                      }}
                    >
                      <SelectTrigger className="w-[180px] h-8 text-sm">
                        <SelectValue placeholder="Filter by Team Member" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Team Members</SelectItem>
                        {teamMembers.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button 
                    variant={filters.show_my_clients ? "default" : "outline"} 
                    size="sm" 
                    onClick={() => {
                      setFilters({ ...filters, show_my_clients: !filters.show_my_clients });
                      // Reset team member filter when clicking "My Clients"
                      if (!filters.show_my_clients) {
                        setSelectedTeamMember("all");
                      }
                    }}
                  >
                    My Clients
                  </Button>
                  <Button variant={filters.no_call_30 ? "default" : "outline"} size="sm" onClick={() => setFilters({ ...filters, no_call_30: !filters.no_call_30 })} data-testid="filter-no-call-30">
                    No call in 30 days
                  </Button>
                  <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm" onClick={() => { clearFilters(); setSelectedTeamMember("all"); }}>
                    Clear Filters
                  </Button>
                </div>
              </div>
            </div>

            {loading ? (
              <p className="text-md3-on-surface-variant">Loading...</p>
            ) : clients.length === 0 ? (
              <div className="text-center py-12">
                <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-md3-on-surface-variant">No clients yet. Create your first client!</p>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="text-center py-12">
                <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-md3-on-surface-variant">No clients match your filters.</p>
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div>
                {/* Bulk Actions Bar */}
                {selectedClients.size > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md3-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-md3-body-small font-medium text-blue-900">
                        {selectedClients.size} client(s) selected
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="default"
                        size="sm"
                        className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setBulkSalesAssignmentDialogOpen(true)}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Assign Sales User
                      </Button>
                      <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                         onClick={() => setSelectedClients(new Set())}>
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="overflow-x-auto">
                  <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedClients.size === currentPageClients.length && currentPageClients.length > 0}
                          onCheckedChange={handleSelectAllClients}
                        />
                      </TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Software</TableHead>
                      <TableHead>Assigned Sales</TableHead>
                      <TableHead>Time Zone</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Last Call</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentPageClients.map((client) => (
                      <TableRow key={client.id} className={isStaleCall(client.last_call_at) ? "bg-red-50 border-l-4 border-l-red-400" : ""}>
                        <TableCell>
                          <Checkbox
                            checked={selectedClients.has(client.id)}
                            onCheckedChange={() => handleClientSelection(client.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{getClientDisplay(client)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{client.client_type || "N/A"}</Badge>
                        </TableCell>
                        <TableCell>{client.industry || "N/A"}</TableCell>
                        <TableCell className="text-md3-body-small">
                          {client.software && client.software.length > 0 
                            ? client.software.join(", ") 
                            : "None"}
                        </TableCell>
                        <TableCell>
                          {clientSalesUsers[client.id] ? (
                            <span className="text-md3-body-small">{clientSalesUsers[client.id].sales_user_name}</span>
                          ) : (
                            <span className="text-xs text-gray-400">Unassigned</span>
                          )}
                        </TableCell>
                        <TableCell>{client.time_zone || "EST"}</TableCell>
                        <TableCell>{client.currency || "USD"}</TableCell>
                        <TableCell data-testid={`client-last-call-${client.id}`}><LastCallCell iso={client.last_call_at} /></TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <LogCallButton entityType="client" entityId={client.id} name={client.client_name} onLogged={(log) => setClients((cs) => withLastCall(cs, log))} />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewClient(client)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewContacts(client)}
                              title="View Contacts"
                            >
                              <Users className="h-4 w-4" />
                            </Button>
                            {/* Edit button - hidden for Sales Team Members */}
                            {!isSalesTeamMember && (
                              <Button size="sm"
                                variant="outline"
                                className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                onClick={() => handleEdit(client)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Delete button - only for Management department */}
                            {user?.department === "Management" && (
                              <Button size="sm"
                                variant="destructive"
                                className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                onClick={() => handleDelete(client.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination Component */}
                {filteredClients.length > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalItems={filteredClients.length}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={[25, 50, 100]}
                    testIdPrefix="clients-pagination"
                  />
                )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Client Details Dialog */}
        <Dialog open={viewClientDialogOpen} onOpenChange={setViewClientDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Client Details</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">Complete information for {viewingClient?.client_name}</DialogDescription>
            </DialogHeader>
            {viewingClient && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-md3-title-large mb-3 text-blue-900">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                    <div>
                      <p className="text-md3-on-surface-variant">Client Number</p>
                      <p className="font-semibold">{viewingClient.client_number}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Client Name</p>
                      <p className="font-semibold">{viewingClient.client_name}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Client Type</p>
                      <p className="font-semibold">{viewingClient.client_type}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Industry</p>
                      <p className="font-semibold">{viewingClient.industry}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Software</p>
                      <p className="font-semibold">{viewingClient.software?.join(", ") || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Business Relation Start Date</p>
                      <p className="font-semibold">{viewingClient.business_relation_start_date || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-md3-title-large mb-3 text-blue-900">Contact Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                    <div>
                      <p className="text-md3-on-surface-variant">Phone</p>
                      <p className="font-semibold">{viewingClient.phone || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Fax</p>
                      <p className="font-semibold">{viewingClient.fax || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Website</p>
                      <p className="font-semibold">{viewingClient.website || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Time Zone</p>
                      <p className="font-semibold">{viewingClient.time_zone}</p>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <h3 className="text-md3-title-large mb-3 text-blue-900">Location</h3>
                  <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                    <div>
                      <p className="text-md3-on-surface-variant">Country</p>
                      <p className="font-semibold">{viewingClient.country || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">State/Province</p>
                      <p className="font-semibold">{viewingClient.state || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Billing Information */}
                <div>
                  <h3 className="text-md3-title-large mb-3 text-blue-900">Billing Information</h3>
                  <div className="grid grid-cols-3 gap-4 text-md3-body-small mb-4">
                    <div>
                      <p className="text-md3-on-surface-variant">Currency</p>
                      <p className="font-semibold">{viewingClient.currency || "USD"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">CCN Hourly Rate</p>
                      <p className="font-semibold">${viewingClient.ccn_hourly_rate || (viewingClient.currency === "CAD" ? 65 : 50)}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Billing Cycle</p>
                      <p className="font-semibold">{viewingClient.billing_cycle_days} days</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-md3-body-small mb-4">
                    <div>
                      <p className="text-md3-on-surface-variant">Approval</p>
                      <p className="font-semibold">{viewingClient.billing_approval || 0}%</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Fabrication</p>
                      <p className="font-semibold">{viewingClient.billing_fabrication || 0}%</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Completion</p>
                      <p className="font-semibold">{viewingClient.billing_completion || 0}%</p>
                    </div>
                  </div>
                  <div className="text-md3-body-small">
                    <p className="text-md3-on-surface-variant mb-1">Billing Address</p>
                    <p className="font-semibold">
                      {viewingClient.billing_address_line1 && <>{viewingClient.billing_address_line1}<br /></>}
                      {viewingClient.billing_address_line2 && <>{viewingClient.billing_address_line2}<br /></>}
                      {viewingClient.billing_city && <>{viewingClient.billing_city}, </>}
                      {viewingClient.state && <>{viewingClient.state} </>}
                      {viewingClient.billing_zip}
                    </p>
                  </div>
                </div>

                {/* Sales Assignment Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-md3-title-large text-blue-900">Sales Assignment</h3>
                    {(user?.department === "Management" || user?.department === "Admin" || user?.department === "PMO") && (
                      <Button size="sm" 
                        className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleOpenSalesAssignment(viewingClient)}>
                        {clientSalesUsers[viewingClient?.id] ? "Reassign Sales User" : "Assign Sales User"}
                      </Button>
                    )}
                  </div>
                  <div className="text-md3-body-small">
                    <div className="mb-2">
                      <p className="text-md3-on-surface-variant">Current Sales User</p>
                      <p className="font-semibold">
                        {clientSalesUsers[viewingClient?.id]?.sales_user_name || (
                          <span className="text-gray-400">No sales user assigned</span>
                        )}
                      </p>
                      {clientSalesUsers[viewingClient?.id] && (
                        <p className="text-xs text-md3-on-surface-variant mt-1">
                          Since: {clientSalesUsers[viewingClient?.id]?.start_date}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <CallHistory entityType="client" entityId={viewingClient.id} name={viewingClient.client_name} onLogged={(log) => setClients((cs) => withLastCall(cs, log))} />

                <div className="flex justify-end pt-4 border-t">
                  <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setViewClientDialogOpen(false)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Sales Assignment Dialog */}
        <Dialog open={salesAssignmentDialogOpen} onOpenChange={setSalesAssignmentDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Assign Sales User</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Assign a sales user to {salesAssignmentClient?.client_name}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateAssignment} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="sales_user_id" className="text-md3-label-large text-gray-700 font-medium">Sales User *</Label>
                <Select 
                  value={assignmentFormData.sales_user_id} 
                  onValueChange={(value) => setAssignmentFormData({ ...assignmentFormData, sales_user_id: value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select sales user" />
                  </SelectTrigger>
                  <SelectContent>
                    {salesAssignableUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.department})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="start_date" className="text-md3-label-large text-gray-700 font-medium">Start Date *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="start_date"
                  type="date"
                  value={assignmentFormData.start_date}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, start_date: e.target.value })}
                />
                <p className="text-xs text-md3-on-surface-variant">
                  Previous assignment will end automatically on {assignmentFormData.start_date ? 
                    new Date(new Date(assignmentFormData.start_date).getTime() - 86400000).toISOString().split('T')[0] : 
                    'the day before'
                  }
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="text-md3-label-large text-gray-700 font-medium">Notes (Optional)</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="notes"
                  placeholder="Reason for assignment or any notes..."
                  value={assignmentFormData.notes}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Assignment History */}
              {salesAssignmentHistory.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Assignment History</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {salesAssignmentHistory.map((assignment) => {
                      const assignedUser = users.find(u => u.id === assignment.sales_user_id);
                      return (
                        <div key={assignment.id} className="p-3 bg-md3-surface rounded text-md3-body-small">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">
                                {assignedUser?.first_name} {assignedUser?.last_name}
                              </p>
                              <p className="text-xs text-md3-on-surface-variant">
                                {assignment.start_date} → {assignment.end_date || "Present"}
                              </p>
                              {assignment.notes && (
                                <p className="text-xs text-md3-on-surface-variant mt-1">{assignment.notes}</p>
                              )}
                            </div>
                            {assignment.is_active && (user?.department === "Management" || user?.department === "Admin" || user?.department === "PMO") && (
                              <Button type="button"
                                size="sm"
                                variant="outline"
                                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleEndAssignment(assignment.id, salesAssignmentClient.id)}>
                                End Now
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setSalesAssignmentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  Assign Sales User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Errors Dialog */}
        <Dialog open={uploadErrorsDialogOpen} onOpenChange={setUploadErrorsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Bulk Upload Results</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {uploadSuccessCount > 0 && (
                  <span className="text-green-600 font-semibold">
                    ✓ Successfully imported {uploadSuccessCount} records
                  </span>
                )}
                {uploadErrors.length > 0 && (
                  <span className="text-red-600 font-semibold block mt-1">
                    ✗ {uploadErrors.length} errors found
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {uploadErrors.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-red-600">Errors:</h4>
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {uploadErrors.map((error, index) => (
                    <div key={index} className="p-3 bg-red-50 border border-red-200 rounded text-md3-body-small">
                      <p className="font-medium text-red-800">{error}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end pt-4 border-t">
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setUploadErrorsDialogOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Preview Dialogs */}
        <BulkImportPreview
          open={importPreviewOpen}
          onClose={() => setImportPreviewOpen(false)}
          previewData={importPreviewData}
          onApply={handleApplyImport}
          entityType={importEntityType}
        />

        <BulkImportPreview
          open={contactsImportPreviewOpen}
          onClose={() => setContactsImportPreviewOpen(false)}
          previewData={contactsPreviewData}
          onApply={handleApplyContactsImport}
          entityType="Contacts"
        />

        {/* Bulk Sales Assignment Dialog */}
        <Dialog open={bulkSalesAssignmentDialogOpen} onOpenChange={setBulkSalesAssignmentDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Assign Sales User to {selectedClients.size} Client(s)</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Assign a sales user to the selected clients
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleBulkSalesAssignment} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="bulk_sales_user" className="text-md3-label-large text-gray-700 font-medium">Sales User *</Label>
                <Select
                  value={bulkAssignmentFormData.sales_user_id}
                  onValueChange={(value) => setBulkAssignmentFormData({ ...bulkAssignmentFormData, sales_user_id: value })}
                  required
                >
                  <SelectTrigger id="bulk_sales_user">
                    <SelectValue placeholder="Select sales user" />
                  </SelectTrigger>
                  <SelectContent>
                    {salesAssignableUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.department})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk_start_date" className="text-md3-label-large text-gray-700 font-medium">Start Date *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="bulk_start_date"
                  type="date"
                  value={bulkAssignmentFormData.start_date}
                  onChange={(e) => setBulkAssignmentFormData({ ...bulkAssignmentFormData, start_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk_notes" className="text-md3-label-large text-gray-700 font-medium">Notes</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="bulk_notes"
                  placeholder="Optional notes about this assignment"
                  value={bulkAssignmentFormData.notes}
                  onChange={(e) => setBulkAssignmentFormData({ ...bulkAssignmentFormData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setBulkSalesAssignmentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >Assign to {selectedClients.size} Client(s)</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
